import { GitBranchIcon, GitPullRequestArrowIcon } from 'lucide-react';
import { Fragment } from 'react';

import { ExternalLink } from '@/shared/components/ExternalLink';
import { isWebAddress } from '@/shared/components/webAddress';
import type {
  TaskArtifactView,
  TaskArtifactViewKind,
} from '@/lib/api/generated/model';
import { ARTIFACT_KIND_WORD, artifactRef, shortCommit } from '../artifactText';
import type { ForgeLinks } from '../forgeLinks';
import { ForgeLink } from './ForgeLink';
import type { RunLabel } from '../runLabel';
import { RunLabelText } from './RunLabelText';

const KIND_ICON: Record<TaskArtifactViewKind, typeof GitBranchIcon> = {
  merge_request: GitPullRequestArrowIcon,
  pull_request: GitPullRequestArrowIcon,
  branch: GitBranchIcon,
};

/**
 * One thing the task created (spec 119 FR-003, FR-004).
 *
 * A merge or pull request leads with its title, linked to the forge through
 * the shared external link; its reference and branches follow, then who
 * opened it and who pushed to it since. A branch, its repository and the
 * branches a request names link to the forge too, by addresses `forgeLinks`
 * reads off a request in the same repository (it supersedes research R5's
 * "branches are text": people expected to click them).
 */
export function ArtifactEntry({
  artifact,
  runLabels,
  links,
}: {
  artifact: TaskArtifactView;
  runLabels: ReadonlyMap<string, RunLabel>;
  links: ForgeLinks;
}) {
  const Icon = KIND_ICON[artifact.kind];
  const kindWord = ARTIFACT_KIND_WORD[artifact.kind];
  const isBranch = artifact.kind === 'branch';
  const ref = artifactRef(artifact);
  const creator = artifact.created_by_execution_id
    ? runLabels.get(artifact.created_by_execution_id)
    : undefined;
  const updaters = (artifact.updated_by ?? []).flatMap((update) => {
    const label = runLabels.get(update.execution_id);
    return label ? [{ id: update.execution_id, label }] : [];
  });
  const commit = shortCommit(artifact.latest_commit);

  return (
    <li className="flex min-w-0 flex-col gap-0.5">
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 text-sm">
        <Icon
          aria-hidden="true"
          className="text-muted-foreground size-3.5 shrink-0 self-center"
        />
        {isBranch ? (
          <span className="text-foreground [overflow-wrap:anywhere]">
            <ForgeLink href={links.repository(artifact)}>
              {artifact.repository}
            </ForgeLink>{' '}
            <ForgeLink
              href={links.branch(artifact, artifact.source_branch)}
              className="font-mono"
            >
              {artifact.source_branch}
            </ForgeLink>
          </span>
        ) : artifact.web_url && isWebAddress(artifact.web_url) ? (
          <ExternalLink href={artifact.web_url}>
            {artifact.title?.trim() || ref}
          </ExternalLink>
        ) : (
          <span className="[overflow-wrap:anywhere]">
            {artifact.title?.trim() || ref}
          </span>
        )}
        {kindWord ? (
          <span className="text-muted-foreground text-xs">{kindWord}</span>
        ) : null}
      </div>
      <p className="text-muted-foreground text-xs [overflow-wrap:anywhere]">
        {isBranch ? (
          commit ? (
            <span className="font-mono">{commit}</span>
          ) : null
        ) : (
          <>
            <span>{ref}</span>
            <span aria-hidden="true"> · </span>
            <span className="font-mono">
              <ForgeLink href={links.branch(artifact, artifact.source_branch)}>
                {artifact.source_branch}
              </ForgeLink>
              {artifact.target_branch ? (
                <>
                  {' → '}
                  <ForgeLink
                    href={links.branch(artifact, artifact.target_branch)}
                  >
                    {artifact.target_branch}
                  </ForgeLink>
                </>
              ) : null}
            </span>
          </>
        )}
      </p>
      {creator ? (
        <p className="text-muted-foreground text-xs">
          <span>{isBranch ? 'Pushed by' : 'Opened by'}</span>{' '}
          <RunLabelText label={creator} />
        </p>
      ) : null}
      {updaters.length > 0 ? (
        <p className="text-muted-foreground text-xs">
          <span>Updated by</span>{' '}
          {updaters.map(({ id, label }, index) => (
            <Fragment key={id}>
              {index > 0 ? ', ' : null}
              <RunLabelText label={label} />
            </Fragment>
          ))}
        </p>
      ) : null}
    </li>
  );
}
